import { Box } from '@mui/material';
import type { PropsWithChildren } from 'react';
import React from 'react';
import { match } from 'ts-pattern';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import LoginProviderButton from './login-provider-button';
import HtmlBox from '../../../component_library/components/HtmlBox/HtmlBox';
import Typography, {
  TypographyVariant,
} from '../../../component_library/components/Typography';
import { LoadingOverlay } from '../../../components/abc/loading-overlay/loading-overlay';
import {
  replaceVariablesInHtml,
  replaceVariablesInString,
} from '../../../dotcms/dotcms-models/converters-from-dotcms';
import {
  LoginMethodElementIds,
  useLoginSsoForm,
} from './hooks/use-login-sso-form';
import type { ITheme } from '../../../component_library/styles/theme';
import type { ILoginProvider } from './hooks/use-login-providers';
import {
  AnchorButton,
  ButtonVariant,
  LinkButton,
} from '../../../component_library/components/Button';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';
import { BoxWithId } from '../../../component_library/components/BoxWithId/BoxWithId';

export const LoginFormBox: React.FC<
PropsWithChildren<
IPropsWithTestId & {
  colors: ITheme['colors'];
  isWithBorders: boolean;
}
>
> = ({
  colors, isWithBorders, children, dataElementId,
}) => (
  <BoxWithId
    sx={{
      display: 'flex',
      flexDirection: 'column',
      rowGap: '40px',
      ...(isWithBorders
        ? {}
        : {
          padding: '64px 44px',
          background: colors.grayscale.white,
          borderRadius: '10px',
          boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.25)',
        }),
    }}
    dataElementId={dataElementId}
  >
    {children}
  </BoxWithId>
);

const LoginSsoForm: React.FC = () => {
  const {
    theme,
    isMobile,
    data,
    loading,
    error,
    customDynamicVariableProvider,
    basicLoginProviders,
    extraLoginProviders,
  } = useLoginSsoForm();
  const themeToken = useThemeToken();

  if (loading) {
    return <LoadingOverlay />;
  }

  if (error || !data) {
    return null;
  }

  const externalLoginButtonsBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
    '> a:not(:last-child)': {
      marginBottom: '24px',
    },
  };

  const renderLoginButtonTitle = (loginProvider: ILoginProvider) => {
    if (!data) {
      return null;
    }

    const overridedLoginMethodTitle = data.loginMethodsButtonText.find(
      (x) => x.key.toLowerCase() === loginProvider.loginMethod.toLowerCase(),
    )?.value;
    const dynamicVariables = {
      [customDynamicVariableProvider]: loginProvider.name ?? '',
    };

    if (overridedLoginMethodTitle) {
      return replaceVariablesInString(
        overridedLoginMethodTitle,
        dynamicVariables,
      );
    }

    const titleHtml = match({
      providerName: loginProvider.name,
    })
      .with(
        {
          providerName: undefined,
        },
        () => data.activeEmployeeLoginName,
      )
      .otherwise(() => data.loginMethodActiveButtonText);

    return (
      <HtmlBox html={replaceVariablesInHtml(titleHtml, dynamicVariables)} />
    );
  };

  const extraLoginLinks = [];
  if (extraLoginProviders.formerEmployeeLoginProvider) {
    extraLoginLinks.push({
      ...extraLoginProviders.formerEmployeeLoginProvider,
      name: data.formerEmployeeLoginName,
      dataElementId: DataElementId.LOGIN_PAGE_FORMER_EMPLOYEE_LOGIN_LINK,
    });
  }
  if (extraLoginProviders.adminLoginProvider) {
    extraLoginLinks.push({
      ...extraLoginProviders.adminLoginProvider,
      name: data.adminLoginName,
      dataElementId: DataElementId.LOGIN_PAGE_ADMIN_LOGIN_LINK,
    });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '24px',
      }}
    >
      <LoginFormBox
        colors={theme.colors}
        isWithBorders={isMobile}
        dataElementId={DataElementId.LOGIN_PAGE_SSO_FORM}
      >
        <Typography
          variant={TypographyVariant.H4}
          additionalStyles={{ textAlign: 'center' }}
        >
          <HtmlBox html={data.headline} />
        </Typography>
        <Box sx={externalLoginButtonsBoxStyle}>
          {basicLoginProviders.map((provider) => (
            <LoginProviderButton
              key={provider.loginMethod.toString() + provider.url}
              loginUrl={provider.url}
              isExternalUrl={provider.isExternalUrl}
              dataElementId={
                LoginMethodElementIds[provider.loginMethod] ??
                DataElementId.LOGIN_PAGE_PROVIDER_LOGIN_BUTTON_UNDEFINED_METHOD
              }
              iconUrl={provider.iconUrl}
              color={provider.providerColor}
              buttonVariant={
                !provider.providerColor && basicLoginProviders.length === 1
                  ? ButtonVariant.PRIMARY
                  : undefined
              }
              themeToken={themeToken}
            >
              {renderLoginButtonTitle(provider)}
            </LoginProviderButton>
          ))}
        </Box>
      </LoginFormBox>
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}>
        {extraLoginLinks.map(({
          url, name, isExternalUrl, dataElementId,
        }) =>
          (isExternalUrl ? (
            <AnchorButton
              key={url}
              href={url}
              target="_self"
              dataElementId={dataElementId}
              themeToken={themeToken}
              variant={ButtonVariant.FLAT}
              inlineButton
            >
              <HtmlBox html={name} />
            </AnchorButton>
          ) : (
            <LinkButton
              key={url}
              to={url}
              dataElementId={dataElementId}
              themeToken={themeToken}
              variant={ButtonVariant.FLAT}
              inlineButton
            >
              <HtmlBox html={name} />
            </LinkButton>
          )))}
      </Box>
    </Box>
  );
};

export default LoginSsoForm;
